import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import InstallerShell from "@/components/installer/InstallerShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import StatusBadge from "@/components/installer/StatusBadge";
import ProgressRingLight from "@/components/installer/ProgressRingLight";
import { useAuth } from "@/context/AuthContext";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { BulkCheckbox } from "@/components/bulk/BulkCheckbox";
import BulkActionBar from "@/components/bulk/BulkActionBar";
import BulkDeleteModal from "@/components/bulk/BulkDeleteModal";
import { reportBulkDeleteResult } from "@/components/bulk/reportBulkDelete";
import {
    Search, X, LayoutGrid, List, ArrowUpDown, ChevronDown, Check,
    GitBranch, User as UserIcon, MapPin, Zap, Flag, Hourglass, MessageSquare,
    CheckSquare, FileText, Calendar, MoreHorizontal, Pause, Play, ArrowRight,
    Upload as UploadIcon, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { projectCompletionField, projectStageLabel } from "@/lib/projectPresentation";

/**
 * InstallerProjects — /installer/projects operational hub.
 *
 * Mirrors the mobile `frontend/app/installer/projects.tsx` file screen-for-
 * screen (adapted from React Native primitives to web): sticky filter bar
 * with search + state tabs + stage / engineer chips + sort dropdown + card /
 * table view toggle, then either a responsive card grid or a dense sortable
 * table. Every card / row exposes the same quick actions: Open, Schedule,
 * Message, Upload, More (put on hold / reactivate).
 */

const SORT_OPTIONS = [
    { key: "next_appointment", label: "Next appointment" },
    { key: "current_phase",    label: "Current phase" },
    { key: "progress",         label: "Progress %" },
    { key: "customer_name",    label: "Customer name" },
    { key: "date_created",     label: "Date created" },
];
const STATE_TABS = [
    { key: "all",       label: "All" },
    { key: "active",    label: "Active" },
    { key: "on_hold",   label: "On Hold" },
    { key: "completed", label: "Completed" },
];
const PROGRESS_FILTERS = {
    in_progress: { label: "In progress",    predicate: (p) => (p.progress || 0) > 0 && (p.progress || 0) < 100 },
    completed:   { label: "Progress: 100%", predicate: (p) => (p.progress || 0) >= 100 },
    awaiting:    { label: "Awaiting kickoff", predicate: (p) => (p.progress || 0) === 0 },
};
const STATE_KEYS = new Set(STATE_TABS.map((t) => t.key));

function formatVisitShort(next) {
    if (!next?.scheduled_at) return "—";
    try {
        const d = new Date(next.scheduled_at);
        if (isNaN(d.getTime())) return next.scheduled_at;
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch { return String(next.scheduled_at); }
}
function formatVisitFull(next) {
    if (!next?.scheduled_at) return "No visit scheduled";
    try {
        const d = new Date(next.scheduled_at);
        if (isNaN(d.getTime())) return next.scheduled_at;
        const day = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
        return next.arrival_window ? `${day} · ${next.arrival_window}` : day;
    } catch { return String(next.scheduled_at); }
}

export default function InstallerProjects() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = !!user?.is_security_admin;
    const canManageProjects = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [search, setSearch] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    // UAT blocker fix · Default Engineer Projects to the active workload
    // only. Completed/archived projects remain reachable through the
    // explicit "Completed" chip. Operations-bridged users (who arrive
    // here in a fallback path) also default to active for parity with
    // the Operations master list.
    const [stateFilter, setStateFilter] = useState(() => {
        const s = searchParams.get("state");
        if (s && STATE_KEYS.has(s)) return s;
        return "active";
    });
    const [progressFilter, setProgressFilter] = useState(() => {
        const p = searchParams.get("progress");
        return p && PROGRESS_FILTERS[p] ? p : null;
    });
    // DEV-003G.2 — Engineer role default: show only projects assigned to the
    // signed-in engineer unless the URL explicitly opts out with ?assigned_to_me=false.
    // Operations users hit /operations/projects for portfolio-wide visibility.
    // FIELD-001B / FIELD-001B1 · The backend `/projects/summary` now
    // returns strictly the engineer's authorised set. A client-side
    // "lead only" narrowing on top produced the misleading "3 of 6"
    // total. For ordinary Engineers (no `manage_projects`), the
    // toggle is FORCED off regardless of the URL param so bookmarks
    // to `?assigned_to_me=true` never re-introduce the tenant-wide
    // denominator. Ops-role users can still opt in via query string.
    const [assignedToMe, setAssignedToMe] = useState(() => {
        if (!canManageProjects) return false;
        const raw = searchParams.get("assigned_to_me");
        if (raw === "true") return true;
        return false;
    });
    const [myEngineerId, setMyEngineerId] = useState(null);
    const [stage, setStage] = useState(null);
    const [engineer, setEngineer] = useState(null);
    const [sort, setSort] = useState("next_appointment");
    const [viewMode, setViewMode] = useState("card");

    // Quick action modals
    const [msgFor, setMsgFor] = useState(null);
    const [msgText, setMsgText] = useState("");
    const [msgSending, setMsgSending] = useState(false);
    const [apptFor, setApptFor] = useState(null);

    const load = useCallback(async (silent = false) => {
        silent ? setRefreshing(true) : setLoading(true);
        try {
            setProjects(await api.listProjectsSummary());
        } catch { /* best-effort */ }
        finally { setLoading(false); setRefreshing(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // DEV-003G.2 — Resolve the current engineer's id (email first, name
    // fallback for the seeded demo). Used to filter `lead_engineer_id ===
    // myEngineerId` — the same source of truth DEV-003A /
    // OperationsProjectDetail rely on.
    useEffect(() => {
        if (!user?.email && !user?.name) return;
        (async () => {
            try {
                const engs = await api.listEngineers({ includeArchived: false });
                const list = engs || [];
                const email = (user.email || "").toLowerCase();
                const name = (user.name || "").toLowerCase();
                const mine =
                    (email && list.find((e) => (e.email || "").toLowerCase() === email)) ||
                    (name && list.find((e) => (e.displayName || "").toLowerCase() === name));
                setMyEngineerId(mine?.id || null);
            } catch { setMyEngineerId(null); }
        })();
    }, [user?.email, user?.name]);

    const stageOptions = useMemo(() => {
        const set = new Set();
        for (const p of projects) if (p.current_phase) set.add(p.current_phase);
        return Array.from(set).sort();
    }, [projects]);
    const engineerOptions = useMemo(() => {
        if (!canManageProjects) return [];
        const set = new Set();
        for (const p of projects) if (p.assigned_engineer) set.add(p.assigned_engineer);
        return Array.from(set).sort();
    }, [projects, canManageProjects]);

    // FIELD-001B1 · Force the Any-engineer filter off for ordinary
    // Engineers even when a stale URL/bookmark supplies ?engineer=...
    // FIELD-001B2 · Also strip the parameter from the URL so browser
    // back / copied deep links do not silently re-apply it.
    useEffect(() => {
        if (canManageProjects) return;
        if (engineer) setEngineer(null);
        if (searchParams.has("engineer")) {
            const next = new URLSearchParams(searchParams);
            next.delete("engineer");
            setSearchParams(next, { replace: true });
        }
    }, [canManageProjects, engineer, searchParams, setSearchParams]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const progPred = progressFilter ? PROGRESS_FILTERS[progressFilter]?.predicate : null;
        // Only apply the "assigned to me" filter once we know the engineer id.
        // If we couldn't resolve one (e.g. ops-bridged user), skip the filter
        // rather than hiding every project.
        const applyMine = assignedToMe && !!myEngineerId;
        let arr = projects.filter((p) => {
            if (applyMine && p.lead_engineer_id !== myEngineerId) return false;
            if (stateFilter !== "all" && (p.state || "active") !== stateFilter) return false;
            if (progPred && !progPred(p)) return false;
            if (stage && p.current_phase !== stage) return false;
            if (engineer && p.assigned_engineer !== engineer) return false;
            if (q) {
                const bag = [p.customer_name, p.address, p.postcode, p.reference, p.system_summary]
                    .filter(Boolean).join(" ").toLowerCase();
                if (!bag.includes(q)) return false;
            }
            return true;
        });
        arr = arr.slice().sort((a, b) => {
            switch (sort) {
                case "customer_name":  return (a.customer_name || "").localeCompare(b.customer_name || "");
                case "progress":       return (b.progress || 0) - (a.progress || 0);
                case "current_phase":  return (a.current_phase || "").localeCompare(b.current_phase || "");
                case "date_created":   return (b.created_at || "").localeCompare(a.created_at || "");
                case "next_appointment":
                default: {
                    const av = a.next_appointment?.scheduled_at || "9999";
                    const bv = b.next_appointment?.scheduled_at || "9999";
                    return av.localeCompare(bv);
                }
            }
        });
        return arr;
    }, [projects, search, stateFilter, progressFilter, stage, engineer, sort, assignedToMe, myEngineerId]);

    // Sync URL with active filters. Removes params when at defaults so deep
    // links and browser Back/Forward behave predictably. `assigned_to_me=true`
    // is the default state for engineers so we only serialise the negative
    // (=false) when the user has explicitly cleared the chip.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (stateFilter && stateFilter !== "all") next.set("state", stateFilter);
        else next.delete("state");
        if (progressFilter) next.set("progress", progressFilter);
        else next.delete("progress");
        if (assignedToMe === false) next.set("assigned_to_me", "false");
        else next.delete("assigned_to_me");
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateFilter, progressFilter, assignedToMe]);

    const setState = async (project, next) => {
        setProjects((prev) => prev.map((p) => p.id === project.id ? { ...p, state: next } : p));
        try {
            await api.updateProjectState(project.id, next);
            toast.success(next === "on_hold" ? "Project put on hold" : "Project reactivated");
            load(true);
        } catch { toast.error("Could not update state"); }
    };

    const sendMessage = async () => {
        if (!msgFor || !msgText.trim()) return;
        setMsgSending(true);
        try {
            await api.sendMessage(msgFor.id, msgText.trim());
            toast.success("Message sent");
            setMsgFor(null); setMsgText(""); load(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
        } finally { setMsgSending(false); }
    };

    const clearFilters = () => { setSearch(""); setStateFilter("all"); setProgressFilter(null); setStage(null); setEngineer(null); setAssignedToMe(false); };

    // ─── Bulk delete (admin-only) ───────────────────────────────────────
    const visibleIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
    const bulk = useBulkSelection(visibleIds);
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    useEffect(() => {
        bulk.pruneToKnown(projects.map((p) => p.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);
    async function handleBulkDone(result) {
        reportBulkDeleteResult(result, { labelSingular: "project", labelPlural: "projects" });
        bulk.clearAll();
        setBulkModalOpen(false);
        await load(true);
    }

    const openCallbacks = {
        onOpen: (p) => navigate(`/installer/project/${p.id}`),
        onScheduleVisit: (p) => setApptFor(p),
        onSendMessage: (p) => { setMsgFor(p); setMsgText(""); },
        onUploadDocument: (p) => navigate(`/documents?project=${p.id}`),
    };

    return (
        <InstallerShell>
            <PageContainer width="full" testid="projects-hub">
                <PageHeader
                    eyebrow={canManageProjects ? "OPERATIONAL HUB" : "MY WORK"}
                    title={canManageProjects ? "Projects" : "My projects"}
                    subtitle={canManageProjects
                        ? "Every customer installation — searchable, filterable and actionable."
                        : "Projects where you are Project Lead or have assigned work."}
                    backFallback="/installer"
                    testid="projects-header"
                    rightSlot={(
                        <button
                            data-testid="refresh-projects"
                            onClick={() => load(true)}
                            className="pe-btn-outline text-sm h-10"
                        >
                            <RefreshCw size={14} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
                            {refreshing ? "Refreshing…" : "Refresh"}
                        </button>
                    )}
                />

                {/* Filter bar */}
                <FilterBar
                    search={search} onSearch={setSearch}
                    stateFilter={stateFilter} onStateFilter={setStateFilter}
                    progressFilter={progressFilter} onProgressFilter={setProgressFilter}
                    assignedToMe={assignedToMe} onAssignedToMe={setAssignedToMe}
                    stage={stage} onStage={setStage} stageOptions={stageOptions}
                    engineer={engineer} onEngineer={setEngineer} engineerOptions={engineerOptions}
                    sort={sort} onSort={setSort}
                    viewMode={viewMode} onViewMode={setViewMode}
                    totalCount={projects.length} filteredCount={filtered.length}
                    canManageProjects={canManageProjects}
                />

                {/* Bulk selection header (admin only) */}
                {isAdmin && filtered.length > 0 && (
                    <div
                        className="mt-4 flex items-center gap-3 text-sm text-[#4B5565]"
                        data-testid="bulk-projects-header"
                    >
                        <BulkCheckbox
                            checked={bulk.allVisibleSelected}
                            indeterminate={bulk.someVisibleSelected}
                            onChange={bulk.toggleAllVisible}
                            ariaLabel="Select all visible projects"
                            testId="bulk-projects-select-all"
                        />
                        <span>
                            {bulk.count > 0
                                ? `${bulk.count} of ${filtered.length} selected`
                                : `Select from ${filtered.length} project${filtered.length === 1 ? "" : "s"}`}
                        </span>
                    </div>
                )}

                {/* Content */}
                <div className="mt-5">
                    {loading ? (
                        <div className="py-16 text-center text-sm text-[#4B5565]">Loading projects…</div>
                    ) : filtered.length === 0 ? (
                        <EmptyState
                            total={projects.length}
                            onClear={clearFilters}
                            filterLabel={
                                stateFilter !== "all"
                                    ? (STATE_TABS.find((t) => t.key === stateFilter)?.label || stateFilter)
                                    : progressFilter
                                        ? PROGRESS_FILTERS[progressFilter]?.label
                                        : assignedToMe
                                            ? "Assigned to me"
                                            : null
                            }
                        />
                    ) : viewMode === "table" ? (
                        <ProjectsTable
                            projects={filtered}
                            {...openCallbacks}
                            bulkEnabled={isAdmin}
                            bulkHas={bulk.has}
                            bulkToggle={bulk.toggle}
                        />
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="projects-grid">
                            {filtered.map((p) => (
                                <ProjectHubCard
                                    key={p.id}
                                    project={p}
                                    onOpen={() => openCallbacks.onOpen(p)}
                                    onScheduleVisit={() => openCallbacks.onScheduleVisit(p)}
                                    onSendMessage={() => openCallbacks.onSendMessage(p)}
                                    onUploadDocument={() => openCallbacks.onUploadDocument(p)}
                                    onMarkOnHold={() => setState(p, "on_hold")}
                                    onMarkActive={() => setState(p, "active")}
                                    bulkEnabled={isAdmin}
                                    bulkChecked={bulk.has(p.id)}
                                    onBulkToggle={() => bulk.toggle(p.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </PageContainer>

            {/* Message dialog */}
            {msgFor && (
                <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-4" data-testid="msg-modal">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md">
                        <div className="text-lg text-[#131B2F] font-medium">Message {msgFor.customer_name}</div>
                        <div className="text-sm text-[#4B5565] mt-0.5">{msgFor.reference} · {msgFor.system_summary}</div>
                        <textarea
                            data-testid="hub-msg-input"
                            value={msgText}
                            onChange={(e) => setMsgText(e.target.value)}
                            rows={4}
                            placeholder="Type your message…"
                            className="mt-4 w-full rounded-md border border-[#E5E7EB] bg-[#F5F5F7] p-3 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                        />
                        <div className="mt-4 flex gap-2">
                            <button className="pe-btn-outline flex-1 justify-center h-11" onClick={() => setMsgFor(null)}>Cancel</button>
                            <button
                                data-testid="hub-msg-send"
                                onClick={sendMessage}
                                disabled={!msgText.trim() || msgSending}
                                className="pe-btn-primary flex-1 justify-center h-11 disabled:opacity-50"
                            >
                                {msgSending ? "Sending…" : "Send"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule visit dialog */}
            {apptFor && <ScheduleVisitModal project={apptFor} onClose={() => setApptFor(null)} onSaved={() => { setApptFor(null); load(true); }} />}

            {isAdmin && (
                <>
                    <BulkActionBar
                        count={bulk.count}
                        labelSingular="project"
                        labelPlural="projects"
                        onClear={bulk.clearAll}
                        onDelete={() => setBulkModalOpen(true)}
                        testidPrefix="bulk-projects"
                    />
                    <BulkDeleteModal
                        open={bulkModalOpen}
                        onCancel={() => setBulkModalOpen(false)}
                        onDone={handleBulkDone}
                        ids={bulk.ids}
                        labelSingular="project"
                        labelPlural="projects"
                        deleter={(batch, ctx) => api.bulkDeleteProjects(batch, ctx)}
                        extraWarning="All linked tasks, appointments and notifications will be removed. Documents are archived (not deleted) and audit history is retained."
                        testidPrefix="bulk-projects-delete"
                    />
                </>
            )}
        </InstallerShell>
    );
}

/* ─────────── Filter bar ─────────── */

function FilterBar({
    search, onSearch, stateFilter, onStateFilter,
    progressFilter, onProgressFilter,
    assignedToMe, onAssignedToMe,
    stage, onStage, stageOptions, engineer, onEngineer, engineerOptions,
    sort, onSort, viewMode, onViewMode, totalCount, filteredCount,
    canManageProjects = true,
}) {
    return (
        <div className="mt-5 pe-card p-4 space-y-4" data-testid="projects-filter-bar">
            {/* Row 1: search + view toggle + sort */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px] sm:min-w-[220px] lg:min-w-[260px] h-10 flex items-center gap-2 px-3 rounded-md border border-[#E5E7EB] bg-[#F5F5F7]">
                    <Search size={16} strokeWidth={1.75} className="text-[#4B5565] shrink-0" />
                    <input
                        data-testid="projects-search"
                        value={search} onChange={(e) => onSearch(e.target.value)}
                        placeholder="Search by customer, postcode, address or reference…"
                        className="flex-1 bg-transparent text-base text-[#131B2F] outline-none placeholder:text-[#4B5565]"
                    />
                    {!!search && (
                        <button data-testid="clear-search" onClick={() => onSearch("")} className="text-[#4B5565]">
                            <X size={14} strokeWidth={1.75} />
                        </button>
                    )}
                </div>

                <div className="flex gap-1 p-[3px] rounded-md border border-[#E5E7EB] bg-[#F5F5F7]">
                    <ToggleBtn active={viewMode === "card"} onClick={() => onViewMode("card")} testid="view-card" icon={LayoutGrid} label="Cards" />
                    <ToggleBtn active={viewMode === "table"} onClick={() => onViewMode("table")} testid="view-table" icon={List} label="Table" />
                </div>

                <Dropdown
                    testid="sort-btn"
                    icon={ArrowUpDown}
                    label={`Sort: ${SORT_OPTIONS.find((s) => s.key === sort)?.label || "Sort"}`}
                    minWidth={190}
                >
                    {(close) => SORT_OPTIONS.map((s) => (
                        <MenuItem
                            key={s.key} testid={`sort-opt-${s.key}`}
                            active={sort === s.key} label={s.label}
                            onClick={() => { onSort(s.key); close(); }}
                        />
                    ))}
                </Dropdown>
            </div>

            {/* Row 2: state tabs + stage / engineer chips + count */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex gap-1 p-[3px] rounded-full border border-[#E5E7EB] bg-[#F5F5F7]">
                    {STATE_TABS.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`state-tab-${t.key}`}
                            onClick={() => onStateFilter(t.key)}
                            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                stateFilter === t.key ? "bg-white text-[#131B2F] shadow-sm" : "text-[#4B5565] hover:text-[#131B2F]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {progressFilter && PROGRESS_FILTERS[progressFilter] && (
                    <span
                        data-testid={`progress-chip-${progressFilter}`}
                        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium bg-[#F1F8EC] text-[#2E7E22] border border-[#3E9E2E]"
                    >
                        <Hourglass size={14} strokeWidth={1.75} className="text-[#2E7E22]" />
                        {PROGRESS_FILTERS[progressFilter].label}
                        <button
                            type="button"
                            data-testid="progress-chip-clear"
                            onClick={() => onProgressFilter(null)}
                            aria-label={`Clear ${PROGRESS_FILTERS[progressFilter].label} filter`}
                            className="ml-0.5 text-[#2E7E22] hover:text-[#1F5B18] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3E9E2E] rounded"
                        >
                            <X size={14} strokeWidth={2} />
                        </button>
                    </span>
                )}

                {assignedToMe && canManageProjects && (
                    <span
                        data-testid="assigned-to-me-chip"
                        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium bg-[#EEF2FF] text-[#3730A3] border border-[#6366F1]/60"
                    >
                        <UserIcon size={14} strokeWidth={1.75} className="text-[#3730A3]" />
                        Assigned to me
                        <button
                            type="button"
                            data-testid="assigned-to-me-clear"
                            onClick={() => onAssignedToMe(false)}
                            aria-label="Show all projects"
                            title="Show all projects"
                            className="ml-0.5 text-[#3730A3] hover:text-[#1F1B72] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] rounded"
                        >
                            <X size={14} strokeWidth={2} />
                        </button>
                    </span>
                )}

                <Dropdown
                    testid="filter-stage-btn"
                    variant={stage ? "brand" : "default"}
                    icon={GitBranch}
                    label={stage ? `Stage: ${stage}` : "Any stage"}
                    onClearActive={stage ? () => onStage(null) : null}
                >
                    {(close) => (
                        <>
                            <MenuItem active={!stage} label="Any stage" testid="stage-opt-any" onClick={() => { onStage(null); close(); }} />
                            {stageOptions.map((o) => (
                                <MenuItem key={o} testid={`stage-opt-${o}`} label={o} active={stage === o} onClick={() => { onStage(o); close(); }} />
                            ))}
                        </>
                    )}
                </Dropdown>

                {/* FIELD-001B2 · The engineer filter is an Operations
                    control (`manage_projects`). Ordinary Engineers must
                    never see "Any engineer" — their authorised set is
                    already tenant-scoped by the backend. */}
                {canManageProjects && (
                <Dropdown
                    testid="filter-engineer-btn"
                    variant={engineer ? "brand" : "default"}
                    icon={UserIcon}
                    label={engineer ? `Engineer: ${engineer}` : "Any engineer"}
                    onClearActive={engineer ? () => onEngineer(null) : null}
                >
                    {(close) => (
                        <>
                            <MenuItem active={!engineer} label="Any engineer" testid="eng-opt-any" onClick={() => { onEngineer(null); close(); }} />
                            {engineerOptions.map((o) => (
                                <MenuItem key={o} testid={`eng-opt-${o}`} label={o} active={engineer === o} onClick={() => { onEngineer(o); close(); }} />
                            ))}
                        </>
                    )}
                </Dropdown>
                )}

                <div className="ml-auto text-sm text-[#4B5565]" data-testid="filter-count">
                    {canManageProjects ? (
                        filteredCount === totalCount ? `${totalCount} projects` : `${filteredCount} of ${totalCount}`
                    ) : (
                        filteredCount === totalCount
                            ? `${filteredCount} authorised project${filteredCount === 1 ? "" : "s"}`
                            : `${filteredCount} matching authorised project${filteredCount === 1 ? "" : "s"}`
                    )}
                </div>
            </div>
        </div>
    );
}

function ToggleBtn({ active, onClick, testid, icon: Icon, label }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                active ? "bg-[#3E9E2E] text-white" : "text-[#4B5565] hover:text-[#131B2F]"
            }`}
        >
            <Icon size={14} strokeWidth={1.75} /> {label}
        </button>
    );
}

function Dropdown({ testid, icon: Icon, label, minWidth = 170, variant = "default", onClearActive, children }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        function onClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);
    const active = variant === "brand";
    return (
        <div ref={ref} className="relative">
            <button
                data-testid={testid}
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium border transition-colors ${
                    active
                        ? "bg-[#F1F8EC] text-[#2E7E22] border-[#3E9E2E]"
                        : "bg-white text-[#131B2F] border-[#E5E7EB] hover:border-[#D1D5DB]"
                }`}
                style={{ minWidth }}
            >
                <Icon size={14} strokeWidth={1.75} className={active ? "text-[#2E7E22]" : "text-[#4B5565]"} />
                <span className="flex-1 text-left truncate">{label}</span>
                {onClearActive ? (
                    <span onClick={(e) => { e.stopPropagation(); onClearActive(); }} className="text-[#2E7E22]">
                        <X size={14} strokeWidth={2} />
                    </span>
                ) : (
                    <ChevronDown size={12} strokeWidth={1.75} className="text-[#4B5565]" />
                )}
            </button>
            {open && (
                <div className="absolute top-11 left-0 z-20 min-w-[220px] max-h-[300px] overflow-y-auto bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1" data-testid="dropdown-menu">
                    {children(() => setOpen(false))}
                </div>
            )}
        </div>
    );
}

function MenuItem({ label, active, onClick, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                active ? "bg-[#F1F8EC] text-[#2E7E22] font-medium" : "text-[#131B2F] hover:bg-[#F5F5F7]"
            }`}
        >
            <span className="truncate">{label}</span>
            {active && <Check size={13} strokeWidth={2} className="text-[#3E9E2E] shrink-0" />}
        </button>
    );
}

/* ─────────── Card view ─────────── */

function ProjectHubCard({ project, onOpen, onScheduleVisit, onSendMessage, onUploadDocument, onMarkOnHold, onMarkActive,
                          bulkEnabled = false, bulkChecked = false, onBulkToggle }) {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    useEffect(() => {
        function onDoc(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
        if (menuOpen) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [menuOpen]);

    const unread = project.unread_customer_messages || 0;
    const status = project.status_label || "on_track";
    const projectLabel = project.reference ? `Project ${project.reference}` : project.customer_name;

    return (
        <div className={`pe-card p-5 flex flex-col gap-4 relative ${bulkChecked ? "ring-2 ring-[#3E9E2E]" : ""}`} data-testid={`hub-card-${project.id}`}>
            {bulkEnabled && (
                <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                    <BulkCheckbox
                        checked={bulkChecked}
                        onChange={() => onBulkToggle?.()}
                        ariaLabel={`Select project ${project.customer_name}`}
                        testId={`bulk-project-card-checkbox-${project.id}`}
                    />
                </div>
            )}
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] text-[#4B5565] font-mono font-medium tracking-[0.05em]">{project.reference}</span>
                        <span className="text-[11px] text-[#4B5565]">·</span>
                        <StatusBadge status={status} size="sm" />
                    </div>
                    <button onClick={onOpen} className="text-left mt-1">
                        <div className="text-xl text-[#131B2F] font-medium tracking-tight truncate hover:text-[#3E9E2E] transition-colors">
                            {project.customer_name}
                        </div>
                    </button>
                    <div className="flex items-center gap-1 mt-1">
                        <MapPin size={13} strokeWidth={1.75} className="text-[#4B5565] shrink-0" />
                        <span className="text-sm text-[#4B5565] truncate">
                            {project.address}{project.postcode ? ` · ${project.postcode}` : ""}
                        </span>
                    </div>
                </div>
                <ProgressRingLight
                    progress={project.progress || 0}
                    size={64}
                    strokeWidth={7}
                    testid={`hub-ring-${project.id}`}
                    onActivate={() => navigate(`/installer/project/${project.id}#phases`)}
                    ariaLabel={`View installation journey for ${projectLabel} — ${project.progress || 0}% complete`}
                />
            </div>

            {/* System pill */}
            <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full max-w-full" style={{ background: "#F1F8EC" }}>
                <Zap size={13} strokeWidth={1.75} className="text-[#2E7E22] shrink-0" />
                <span className="text-sm text-[#2E7E22] font-medium truncate">{project.system_summary}</span>
            </span>

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-3">
                <MetaItem icon={GitBranch} label="Current phase" value={projectStageLabel(project)} />
                <MetaItem icon={Calendar}  label="Next visit"    value={formatVisitFull(project.next_appointment)} />
                <MetaItem
                    icon={UserIcon}
                    label="Project Lead"
                    value={project.lead_engineer_name || "Unassigned"}
                    testid={`hub-project-lead-${project.id}`}
                />
                <MetaItem
                    icon={Hourglass}
                    label={projectCompletionField(project).label}
                    value={projectCompletionField(project).value}
                    testid={`hub-est-${project.id}`}
                />
            </div>

            {/* FIELD-001B · When project access is granted by a task /
                survey / visit rather than by leading the project, show
                the reason clearly so engineers know why they see it. */}
            {project.your_assignment_reason
                && project.your_assignment_reason.kind !== "lead"
                && project.your_assignment_reason.label && (
                <div
                    data-testid={`hub-your-assignment-${project.id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full self-start text-xs font-medium bg-[#EEF2FF] text-[#3730A3]"
                >
                    Your assignment: {project.your_assignment_reason.label}
                </div>
            )}

            {/* Signals */}
            <div className="flex gap-2 flex-wrap pt-3 border-t border-[#E5E7EB]">
                <Signal icon={MessageSquare} value={unread} label="unread" attention={unread > 0} />
                <Signal icon={CheckSquare} value={project.outstanding_tasks || 0} label="tasks" />
                <Signal icon={FileText}    value={project.documents_count || 0} label="docs" />
            </div>

            {/* Actions */}
            <div className="flex gap-1.5 flex-wrap">
                <ActionBtn primary onClick={onOpen} icon={ArrowRight} label="Open" testid={`hub-card-${project.id}-open`} />
                <ActionBtn onClick={onScheduleVisit} icon={Calendar} label="Schedule" testid={`hub-card-${project.id}-schedule`} />
                <ActionBtn onClick={onSendMessage}   icon={MessageSquare} label="Message"  testid={`hub-card-${project.id}-message`} />
                <ActionBtn onClick={onUploadDocument} icon={UploadIcon}  label="Upload"    testid={`hub-card-${project.id}-upload`} />
                <div ref={menuRef} className="relative">
                    <ActionBtn onClick={() => setMenuOpen((v) => !v)} icon={MoreHorizontal} label="More" testid={`hub-card-${project.id}-more`} />
                    {menuOpen && (
                        <div className="absolute top-10 right-0 z-20 min-w-[190px] bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1">
                            {project.state === "on_hold" ? (
                                <MenuRow icon={Play}  label="Mark as active" onClick={() => { setMenuOpen(false); onMarkActive(); }} testid={`hub-card-${project.id}-mark-active`} />
                            ) : (
                                <MenuRow icon={Pause} label="Put on hold"    onClick={() => { setMenuOpen(false); onMarkOnHold(); }} testid={`hub-card-${project.id}-mark-hold`} />
                            )}
                            <MenuRow icon={ArrowRight} label="Open project" onClick={() => { setMenuOpen(false); onOpen(); }} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function MetaItem({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-full bg-[#F5F5F7] text-[#4B5565] flex items-center justify-center shrink-0">
                <Icon size={13} strokeWidth={1.75} />
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] text-[#4B5565] uppercase tracking-[0.06em] font-medium">{label}</div>
                <div className="text-sm text-[#131B2F] font-medium truncate">{value}</div>
            </div>
        </div>
    );
}

function Signal({ icon: Icon, value, label, attention = false }) {
    const on = attention && value > 0;
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm ${
                on ? "border-[#F9C7B1] bg-[#FEEDE5] text-[#B45309]" : "border-[#E5E7EB] bg-[#F5F5F7] text-[#4B5565]"
            }`}
        >
            <Icon size={13} strokeWidth={1.75} />
            <span className={`font-medium ${on ? "text-[#B45309]" : "text-[#131B2F]"}`}>{value}</span>
            <span>{label}</span>
        </span>
    );
}

function ActionBtn({ primary, onClick, icon: Icon, label, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${
                primary
                    ? "bg-[#3E9E2E] border-[#3E9E2E] text-white hover:bg-[#2E7E22]"
                    : "bg-white border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]"
            }`}
        >
            <Icon size={14} strokeWidth={1.75} /> {label}
        </button>
    );
}

function MenuRow({ icon: Icon, label, onClick, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-[#131B2F] hover:bg-[#F5F5F7] transition-colors"
        >
            <Icon size={14} strokeWidth={1.75} className="text-[#4B5565]" /> {label}
        </button>
    );
}

/* ─────────── Table view ─────────── */

function ProjectsTable({ projects, onOpen, onScheduleVisit, onSendMessage, onUploadDocument,
                         bulkEnabled = false, bulkHas, bulkToggle }) {
    const navigate = useNavigate();
    const headers = bulkEnabled
        ? ["", "Customer", "Reference", "System", "Current phase", "Status", "Progress", "Next visit", "Project Lead", "Msgs", "Tasks", "Docs", "Actions"]
        : ["Customer", "Reference", "System", "Current phase", "Status", "Progress", "Next visit", "Project Lead", "Msgs", "Tasks", "Docs", "Actions"];
    return (
        <div className="pe-card overflow-hidden" data-testid="projects-table">
            <div className="overflow-x-auto">
                <table className="min-w-[1280px] w-full">
                    <thead>
                        <tr className="bg-[#F5F5F7] border-b border-[#E5E7EB]">
                            {headers.map((h, idx) => (
                                <th key={`${h}-${idx}`} className="px-3 py-3 text-[11px] font-medium tracking-[0.06em] text-[#4B5565] uppercase text-left">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map((p) => (
                            <tr
                                key={p.id}
                                data-testid={`tbl-row-${p.id}`}
                                onClick={() => onOpen(p)}
                                className="border-b border-[#E5E7EB] cursor-pointer hover:bg-[#F1F8EC]/50 transition-colors"
                            >
                                {bulkEnabled && (
                                    <td className="px-3 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                                        <BulkCheckbox
                                            checked={bulkHas(p.id)}
                                            onChange={() => bulkToggle(p.id)}
                                            ariaLabel={`Select ${p.customer_name}`}
                                            testId={`bulk-project-checkbox-${p.id}`}
                                        />
                                    </td>
                                )}
                                <td className="px-3 py-4 min-w-[220px]">
                                    <div className="text-sm text-[#131B2F] font-medium truncate">{p.customer_name}</div>
                                    <div className="text-[11px] text-[#4B5565] truncate">{p.address}{p.postcode ? ` · ${p.postcode}` : ""}</div>
                                </td>
                                <td className="px-3 py-4 min-w-[120px] text-[11px] text-[#4B5565] font-mono font-medium">{p.reference}</td>
                                <td className="px-3 py-4 min-w-[170px] text-sm text-[#131B2F] truncate">{p.system_summary}</td>
                                <td className="px-3 py-4 min-w-[160px] text-sm text-[#131B2F] truncate" data-testid={`tbl-stage-${p.id}`}>{projectStageLabel(p)}</td>
                                <td className="px-3 py-4 min-w-[170px]"><StatusBadge status={p.status_label || "on_track"} size="sm" /></td>
                                <td className="px-3 py-4 min-w-[120px]" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-2">
                                        <ProgressRingLight
                                            progress={p.progress || 0}
                                            size={32}
                                            strokeWidth={4}
                                            showValue={false}
                                            testid={`tbl-ring-${p.id}`}
                                            onActivate={() => navigate(`/installer/project/${p.id}#phases`)}
                                            ariaLabel={`View installation journey for ${p.reference || p.customer_name} — ${p.progress || 0}% complete`}
                                        />
                                        <span className="text-sm text-[#131B2F] font-medium">{p.progress || 0}%</span>
                                    </div>
                                </td>
                                <td className="px-3 py-4 min-w-[120px] text-sm text-[#131B2F]">{formatVisitShort(p.next_appointment)}</td>
                                <td className="px-3 py-4 min-w-[140px] text-sm text-[#131B2F] truncate" data-testid={`tbl-lead-${p.id}`}>{p.lead_engineer_name || "Unassigned"}</td>
                                <td className={`px-3 py-4 min-w-[60px] text-sm font-medium ${(p.unread_customer_messages || 0) > 0 ? "text-[#B45309]" : "text-[#131B2F]"}`}>
                                    {p.unread_customer_messages || 0}
                                </td>
                                <td className="px-3 py-4 min-w-[60px] text-sm text-[#131B2F]">{p.outstanding_tasks || 0}</td>
                                <td className="px-3 py-4 min-w-[60px] text-sm text-[#131B2F]">{p.documents_count || 0}</td>
                                <td className="px-3 py-4 min-w-[190px]" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-1">
                                        <TableIconBtn testid={`tbl-open-${p.id}`}     onClick={() => onOpen(p)}            icon={ArrowRight}     label="Open" />
                                        <TableIconBtn testid={`tbl-schedule-${p.id}`} onClick={() => onScheduleVisit(p)}   icon={Calendar}       label="Schedule" />
                                        <TableIconBtn testid={`tbl-msg-${p.id}`}      onClick={() => onSendMessage(p)}     icon={MessageSquare}  label="Message" />
                                        <TableIconBtn testid={`tbl-upload-${p.id}`}   onClick={() => onUploadDocument(p)}  icon={UploadIcon}     label="Upload" />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function TableIconBtn({ onClick, icon: Icon, label, testid }) {
    return (
        <button
            data-testid={testid}
            title={label}
            aria-label={label}
            onClick={onClick}
            className="w-8 h-8 rounded-md border border-[#E5E7EB] flex items-center justify-center text-[#3E9E2E] hover:bg-[#F1F8EC] transition-colors"
        >
            <Icon size={14} strokeWidth={1.75} />
        </button>
    );
}

/* ─────────── Empty & Schedule ─────────── */

function EmptyState({ total, onClear, filterLabel }) {
    // DEV-003G.1 — Focused variant when a URL-driven state/progress filter is
    // active but no projects match. Shows the filter chip, one message, one CTA.
    if (total > 0 && filterLabel) {
        return (
            <div
                className="pe-card p-12 flex flex-col items-center gap-3 text-center"
                data-testid="empty-filtered"
            >
                <span className="w-14 h-14 rounded-full bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center">
                    <Search size={26} strokeWidth={1.75} />
                </span>
                <span
                    data-testid="empty-filter-label"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#F1F8EC] text-[#2E7E22] border border-[#3E9E2E]/40 uppercase tracking-[0.08em]"
                >
                    {filterLabel}
                </span>
                <div className="text-lg text-[#131B2F] font-medium">
                    No projects match this filter.
                </div>
                <button
                    onClick={onClear}
                    className="pe-btn-primary text-sm mt-2"
                    data-testid="see-all-projects"
                >
                    See all projects
                </button>
            </div>
        );
    }
    return (
        <div className="pe-card p-12 flex flex-col items-center gap-3 text-center">
            <span className="w-14 h-14 rounded-full bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center">
                {total === 0 ? <FileText size={26} strokeWidth={1.75} /> : <Search size={26} strokeWidth={1.75} />}
            </span>
            <div className="text-lg text-[#131B2F] font-medium">
                {total === 0 ? "No projects yet" : "No projects match your filters"}
            </div>
            <div className="text-sm text-[#4B5565] max-w-md">
                {total === 0
                    ? "Create your first customer project from the Dashboard to see it here."
                    : "Try clearing your search or resetting the filters."}
            </div>
            {total > 0 && (
                <button onClick={onClear} className="pe-btn-primary text-sm mt-2" data-testid="clear-filters">
                    Clear filters
                </button>
            )}
        </div>
    );
}

function ScheduleVisitModal({ project, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: "Site visit",
        engineer_name: project.assigned_engineer || "",
        arrival_window: "9:00 AM – 11:00 AM",
        appointment_type: "Survey",
        scheduled_at: "",
        customer_instructions: "",
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        setErr("");
        if (!form.title || !form.engineer_name || !form.scheduled_at) {
            setErr("Title, engineer and date/time are required");
            return;
        }
        setSaving(true);
        try {
            const iso = new Date(form.scheduled_at).toISOString();
            await api.createAppointment(project.id, { ...form, scheduled_at: iso });
            toast.success("Visit scheduled");
            onSaved();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSaving(false); }
    };

    const fields = [
        ["title", "Title", "text"],
        ["engineer_name", "Engineer name", "text"],
        ["arrival_window", "Arrival window", "text"],
        ["appointment_type", "Type", "text"],
        ["scheduled_at", "Date & time", "datetime-local"],
        ["customer_instructions", "Customer instructions", "textarea"],
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-4" data-testid="schedule-modal">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-lg text-[#131B2F] font-medium">Schedule visit</div>
                        <div className="text-sm text-[#4B5565] mt-0.5">{project.customer_name} · {project.reference}</div>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#F5F5F7] text-[#4B5565] flex items-center justify-center">
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </div>
                <div className="mt-5 space-y-3.5">
                    {fields.map(([k, label, type]) => (
                        <div key={k}>
                            <label className="block text-sm text-[#4B5565] mb-1.5">{label}</label>
                            {type === "textarea" ? (
                                <textarea
                                    data-testid={`sched-${k}`}
                                    value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                    rows={3}
                                    className="w-full rounded-md border border-[#E5E7EB] bg-white p-3 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                                />
                            ) : (
                                <input
                                    data-testid={`sched-${k}`}
                                    type={type} value={form[k]}
                                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                                />
                            )}
                        </div>
                    ))}
                    {!!err && <div className="text-sm text-[#E53E3E]">{err}</div>}
                    <div className="flex gap-2 pt-2">
                        <button className="pe-btn-outline flex-1 justify-center h-11" onClick={onClose}>Cancel</button>
                        <button className="pe-btn-primary flex-1 justify-center h-11" onClick={submit} disabled={saving} data-testid="sched-submit">
                            {saving ? "Scheduling…" : "Schedule"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
